import React, { FormEventHandler, useCallback, useState } from 'react';

import InfoIcon from '@mui/icons-material/Info';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { RefundPreviewMethod } from '~/services/PaymentsService';

import ApiError from '~/types/ApiError';

import currencyFormatter from '~/utils/currencyFormatter';

import WarningExistingExperienceItems from '../../WarningExistingExperienceItems';
import { RefundMetadata, RefundPayload } from '../../types';
import SelectRefundMethod from '../Common/SelectRefundMethod';
import { RefundMethods, buildRefundPayload, convertStrOrBoolToBool } from '../Utils/RefundUtils';
import { RefundGovernanceResult } from '../Utils/refundGovernance';

import RefundPaymentBreakdown from './RefundPaymentBreakdown';

interface Props {
  customerName: string;
  currencyCode: string;
  costCurrencyCode?: string;
  refundPayload: RefundPayload;
  vendorContribution: number;
  LEContribution: number;
  onSubmit: FormEventHandler<HTMLFormElement>;
  decrementStep: () => void;
  refundMethods: Array<RefundMethods>;
  defaultCustomerRefundNotification?: boolean;
  LEProfit: number;
  isSubmitting: boolean;
  refundMetadata: RefundMetadata;
  isWithinCancellationPolicy?: boolean;
  hasCompletedExperiences?: boolean;
  // Payment schedule adjustment props
  adjustPaymentSchedule?: boolean;
  calculatedRefundableAmountForSchedule?: number;
  calculatedAdjustmentAmount?: number;
  currentCustomFeeForAccounting?: number;
  onRefundMethodChange?: (method: RefundMethods) => void;
  paymentBreakdownMethods?: Array<RefundPreviewMethod>;
  isPaymentBreakdownLoading?: boolean;
  paymentBreakdownError?: ApiError | null;
  hideRefundMethod?: boolean;
  defaultRefundMethod?: RefundMethods;
  refundGovernance?: RefundGovernanceResult;
  isGroupPayment?: boolean;
}

export default function RefundConfirmation({
  customerName,
  currencyCode,
  costCurrencyCode,
  refundPayload,
  vendorContribution,
  LEContribution,
  onSubmit,
  decrementStep,
  refundMethods,
  defaultCustomerRefundNotification,
  LEProfit,
  isSubmitting,
  refundMetadata,
  isWithinCancellationPolicy,
  hasCompletedExperiences = false,
  adjustPaymentSchedule = false,
  calculatedRefundableAmountForSchedule,
  calculatedAdjustmentAmount,
  currentCustomFeeForAccounting = 0,
  onRefundMethodChange,
  paymentBreakdownMethods,
  isPaymentBreakdownLoading,
  paymentBreakdownError,
  hideRefundMethod = false,
  defaultRefundMethod,
  refundGovernance,
  isGroupPayment = false,
}: Props) {
  const [openWarningExistingExperienceItems, setOpenWarningExistingExperienceItems] = useState(false);
  const governanceMessages = [
    ...(refundGovernance?.blockingMessages.map((message) => ({ message, severity: 'error' as const })) ?? []),
    ...(refundGovernance?.warningMessages.map((message) => ({ message, severity: 'warning' as const })) ?? []),
  ];

  const finalPayload = buildRefundPayload(refundPayload, refundMetadata, isWithinCancellationPolicy, {
    orderCurrencyCode: currencyCode,
    vendorContribution,
    currentCustomFeeForAccounting,
    isGroupPayment,
  });

  const handleSubmitWithExperiencesWarning = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (refundGovernance?.isBlocked) {
        return;
      }

      if (hasCompletedExperiences) {
        setOpenWarningExistingExperienceItems(true);
      } else {
        const form = document.getElementById('refund-confirmation-form') as HTMLFormElement;
        if (form) {
          const formEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(formEvent);
        }
      }
    },
    [hasCompletedExperiences, refundGovernance?.isBlocked],
  );

  const handleConfirmWarning = useCallback(() => {
    setOpenWarningExistingExperienceItems(false);
    const form = document.getElementById('refund-confirmation-form') as HTMLFormElement;
    if (form) {
      const formEvent = new Event('submit', { bubbles: true, cancelable: true });
      form.dispatchEvent(formEvent);
    }
  }, []);

  const handleCloseWarning = useCallback(() => {
    setOpenWarningExistingExperienceItems(false);
  }, []);

  return (
    <>
      <DialogContent dividers>
        <form onSubmit={onSubmit} id="refund-confirmation-form">
          <Stack spacing={2}>
            <Typography fontWeight="bold">Refund Summary</Typography>
            {/* Refund Summary */}
            <Grid container spacing={2}>
              <Grid size={5}>Help Desk Ticket ID</Grid>
              <Grid size={7}>{refundPayload.ticket_id}</Grid>
              <Grid size={5}>Customer Name</Grid>
              <Grid size={7}>{customerName}</Grid>
              <Grid size={5}>Refund Category</Grid>
              <Grid size={7}>{refundPayload.reason}</Grid>
              <Grid size={5}>Customer's Reason</Grid>
              <Grid size={7}>{refundPayload.comment}</Grid>
              <Grid size={5}>Source of Fund</Grid>
              <Grid size={7}>{refundPayload.accounting_metadata[0].source}</Grid>
              <Grid size={5}>Customer Fee</Grid>
              <Grid size={7}>{currencyFormatter(currencyCode, finalPayload.accounting_metadata[0].refund_fee, 2)}</Grid>
              {refundPayload.update_vcc_balance && (
                <>
                  <Grid size={5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      VCC Balance Adjusted
                      <Tooltip title="For vendors that accept VCC (Virtual Credit Card) payments, their VCC balance will be automatically adjusted to reflect this refund amount.">
                        <InfoIcon fontSize="small" color="action" />
                      </Tooltip>
                    </Stack>
                  </Grid>
                  <Grid size={7}>
                    <Typography>True</Typography>
                  </Grid>
                </>
              )}
              <Grid size={5}>Package Cancelled</Grid>
              <Grid size={7}>
                {convertStrOrBoolToBool(refundPayload.mark_cancelled) ? (
                  <Typography color="red">This package will be cancelled.</Typography>
                ) : (
                  <Typography>This package will not be cancelled.</Typography>
                )}
              </Grid>
              {/* Vendor and LE Contribution Comparison */}
              {costCurrencyCode !== undefined &&
                costCurrencyCode !== currencyCode &&
                !!finalPayload?.additional_info?.vendor_contribution_cost_currency && (
                  <>
                    <Grid size={5}>Vendor Contribution ({costCurrencyCode})</Grid>
                    <Grid size={7}>
                      {currencyFormatter(
                        costCurrencyCode || currencyCode,
                        (finalPayload?.additional_info as any)?.vendor_contribution_cost_currency || 0,
                        2,
                      )}
                    </Grid>
                  </>
                )}
              <Grid size={5}>Vendor Contribution</Grid>
              <Grid size={7}>{currencyFormatter(currencyCode, vendorContribution, 2)}</Grid>
              {LEProfit > 0 && <Grid size={5}>LE Margin</Grid>}
              {LEProfit > 0 && (
                <Grid color="green" size={7}>
                  {currencyFormatter(currencyCode, LEProfit, 2)}
                </Grid>
              )}
              <Grid size={5}>LE Contribution</Grid>
              <Grid size={7}>{currencyFormatter(currencyCode, LEContribution, 2)}</Grid>
              <Grid size={12}>
                <Divider />
              </Grid>
              <Grid size={5}>
                <Typography color="red" fontWeight="bold">
                  {adjustPaymentSchedule ? 'Customer Refund Amount' : 'Total Refund To Customer'}
                </Typography>
              </Grid>
              <Grid size={7}>
                <Typography color="red" fontWeight="bold">
                  {currencyFormatter(
                    currencyCode,
                    adjustPaymentSchedule && calculatedRefundableAmountForSchedule !== undefined
                      ? calculatedRefundableAmountForSchedule
                      : (refundPayload.amount ?? 0),
                    2,
                  )}
                </Typography>
              </Grid>
              {adjustPaymentSchedule && calculatedAdjustmentAmount !== undefined && (
                <>
                  <Grid size={5}>
                    <Typography color="red" fontWeight="bold">
                      Adjusted Amount
                    </Typography>
                  </Grid>
                  <Grid size={7}>
                    <Typography color="red" fontWeight="bold">
                      {currencyFormatter(currencyCode, Math.abs(calculatedAdjustmentAmount), 2)}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
            <Divider />
            <RefundPaymentBreakdown
              currencyCode={currencyCode}
              methods={paymentBreakdownMethods}
              isLoading={isPaymentBreakdownLoading}
              error={paymentBreakdownError}
            />
            {governanceMessages.map(({ message, severity }) => (
              <Alert key={`${severity}-${message}`} severity={severity}>
                {message}
              </Alert>
            ))}
            {hideRefundMethod ? (
              <input type="hidden" name="refundMethod" value="Back To Original" />
            ) : (
              <SelectRefundMethod
                refundMethods={refundMethods}
                defaultValue={defaultRefundMethod}
                onChange={onRefundMethodChange}
              />
            )}
            <FormControl>
              <FormLabel
                id="send-customer-refund-notification"
                required
                sx={{
                  fontWeight: 'bold',
                }}
              >
                Send customer refund notification?
              </FormLabel>
              <RadioGroup
                name="customerRefundNotification"
                row
                defaultValue={defaultCustomerRefundNotification ?? false}
              >
                <FormControlLabel
                  data-cy="send-customer-refund-notification-yes"
                  key="send-customer-refund-notification-yes"
                  value
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  key="send-customer-refund-notification-no"
                  value={false}
                  control={<Radio />}
                  data-cy="send-customer-refund-notification-no"
                  label="No"
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={decrementStep}>Back</Button>
        <Button
          variant="contained"
          onClick={handleSubmitWithExperiencesWarning}
          form="refund-confirmation-form"
          loading={isSubmitting}
          disabled={refundGovernance?.isBlocked}
        >
          Submit
        </Button>
      </DialogActions>
      <WarningExistingExperienceItems
        open={openWarningExistingExperienceItems}
        onClose={handleCloseWarning}
        onConfirm={handleConfirmWarning}
      />
    </>
  );
}
